import React, { useMemo } from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { OccurrenceItemRow } from '@/components/admin/todoLists/OccurrenceItemRow';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { RADIUS, onColor } from '@/constants/shared/theme';
import type {
  AvailableAssignee,
  ItemStatus,
  OccurrenceItem,
  TodoList,
} from '@/types/admin/todoLists';
import { CADENCE_LABEL } from './ToDosList';

// ---------------------------------------------------------------------------
// Pure helpers — shared with the desktop view so the two surfaces never drift.
// ---------------------------------------------------------------------------

export const NO_PERIOD_LABEL = 'No current period';
export const ERROR_LABEL = 'This to-do list is unavailable.';

/**
 * The visible, ordered items for an occurrence: hidden items dropped, then
 * sorted by `sortOrder` ascending. The backend does NOT contractually order the
 * items array, so this client-side sort is mandatory.
 */
export function sortVisibleItems(items: OccurrenceItem[]): OccurrenceItem[] {
  return items
    .filter(i => i.hiddenAt == null)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Build an accountId → display-name map from the staff (assignee) list. */
export function buildAssigneeNameMap(
  assignees: AvailableAssignee[],
): Map<number, string> {
  const map = new Map<number, string>();
  for (const person of assignees) {
    const name = `${person.firstName} ${person.lastName}`.trim();
    map.set(person.id, name || person.email);
  }
  return map;
}

/** Post-save confirmation variant carried by the `savedBanner` nav param. */
export type SavedBannerVariant = 'saved' | 'saved-applied';

/** Transient save-confirmation copy, keyed by the apply-now outcome. */
export const SAVED_BANNER_COPY: Record<SavedBannerVariant, string> = {
  saved: 'Changes saved.',
  'saved-applied': 'Changes saved — current checklist updated.',
};

/** Read-only marker shown where Edit would be for an archived list. */
export const ARCHIVED_READ_ONLY_LABEL = 'Archived — read-only';

/**
 * Transient save-confirmation banner rendered above the checklist on BOTH the
 * mobile and desktop occurrence views (the screen the edit flow lands on).
 * Token colors only; the host owns clearing the driving nav param + the
 * auto-dismiss timer. Renders nothing when there is no active variant.
 */
export const SavedBanner: React.FC<{ variant?: SavedBannerVariant | null }> = ({
  variant,
}) => {
  const { redesign } = useAdminColors();
  if (!variant) return null;
  return (
    <View
      testID="todo-list-saved-banner"
      style={[styles.savedBanner, { backgroundColor: redesign.accent.greenBg }]}
    >
      <Text style={[styles.savedBannerText, { color: redesign.accent.green }]}>
        {SAVED_BANNER_COPY[variant]}
      </Text>
    </View>
  );
};

interface Props {
  list: TodoList;
  assignees: AvailableAssignee[];
  isLoading: boolean;
  isError: boolean;
  onSetItemStatus: (itemId: number, status: ItemStatus) => void;
  /** Back affordance handler — returns to the To Do's index. */
  onBack: () => void;
  /** When set, renders an Edit affordance that opens the edit form. */
  onEditList?: () => void;
  /** Transient post-save confirmation shown above the checklist; null hides it. */
  savedBanner?: SavedBannerVariant | null;
}

/**
 * Mobile occurrence detail: header (title/cadence/period + completed pill) and a
 * card of check-off rows for the current occurrence. Empty/error states are
 * inline, non-crashing text.
 */
export const TodoListOccurrenceView: React.FC<Props> = ({
  list,
  assignees,
  isError,
  onSetItemStatus,
  onBack,
  onEditList,
  savedBanner,
}) => {
  const { redesign, admin } = useAdminColors();
  const nameMap = useMemo(() => buildAssigneeNameMap(assignees), [assignees]);
  const occurrence = list.currentOccurrence;
  const items = useMemo(
    () => (occurrence ? sortVisibleItems(occurrence.items) : []),
    [occurrence],
  );
  const completed = occurrence?.status === 'completed';

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <TouchableOpacity
        testID="todo-list-back-button"
        onPress={onBack}
        accessibilityRole="button"
        accessibilityLabel="Back to To Do's"
        style={styles.backLink}
      >
        <Text style={[styles.backLinkText, { color: redesign.text.secondary }]}>
          ‹ To Do's
        </Text>
      </TouchableOpacity>

      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text
            style={[styles.title, { color: redesign.text.primary }]}
            numberOfLines={2}
          >
            {list.title}
          </Text>
          <View style={styles.headerActions}>
            {completed && (
              <View
                style={[
                  styles.completedPill,
                  { backgroundColor: redesign.accent.greenBg },
                ]}
              >
                <Text
                  style={[
                    styles.completedText,
                    { color: redesign.accent.green },
                  ]}
                >
                  Completed
                </Text>
              </View>
            )}
            {onEditList ? (
              // Filled accent treatment matching the desktop header's primary
              // Edit action (and the index's "New to-do list") — a peer action,
              // not a whisper. Foreground ink is derived from the fill so it
              // stays readable in both themes.
              <TouchableOpacity
                testID="todo-list-edit-button"
                onPress={onEditList}
                accessibilityRole="button"
                accessibilityLabel="Edit list"
                style={[
                  styles.editButton,
                  { backgroundColor: admin.formInput.borderFocused },
                ]}
              >
                <Text
                  style={[
                    styles.editText,
                    { color: onColor(admin.formInput.borderFocused) },
                  ]}
                >
                  Edit list
                </Text>
              </TouchableOpacity>
            ) : list.status === 'archived' ? (
              <Text style={[styles.editText, { color: redesign.text.muted }]}>
                {ARCHIVED_READ_ONLY_LABEL}
              </Text>
            ) : null}
          </View>
        </View>
        <Text style={[styles.subtitle, { color: redesign.text.muted }]}>
          {CADENCE_LABEL[list.cadence]}
          {occurrence ? ` · ${occurrence.periodDate}` : ''}
        </Text>
      </View>

      <SavedBanner variant={savedBanner} />

      {isError ? (
        <Text style={[styles.stateText, { color: redesign.text.muted }]}>
          {ERROR_LABEL}
        </Text>
      ) : !occurrence ? (
        <Text style={[styles.stateText, { color: redesign.text.muted }]}>
          {NO_PERIOD_LABEL}
        </Text>
      ) : (
        <GlassCard variant="soft" style={styles.card}>
          {items.map((item, index) => (
            <View key={item.id}>
              {index > 0 && (
                <View
                  style={[
                    styles.divider,
                    { backgroundColor: redesign.separator },
                  ]}
                />
              )}
              <OccurrenceItemRow
                item={item}
                completedByName={
                  item.completedByAccountId != null
                    ? nameMap.get(item.completedByAccountId)
                    : undefined
                }
                onChange={status => onSetItemStatus(item.id, status)}
              />
            </View>
          ))}
        </GlassCard>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 32, gap: 16 },
  backLink: { alignSelf: 'flex-start', paddingVertical: 4 },
  // Font size intentionally left at the platform default (14 — the same size
  // the survey back-link sets explicitly) to avoid a new inline typographic
  // literal; color uses the redesign namespace rather than the survey screen's
  // residents link token.
  backLinkText: { fontWeight: '600' },
  header: { gap: 6 },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: { flex: 1, fontSize: 20, fontWeight: '700' },
  subtitle: { fontSize: 13 },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  completedPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  completedText: { fontSize: 11, fontWeight: '700' },
  editButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: RADIUS.sm,
  },
  editText: { fontSize: 13, fontWeight: '600' },
  stateText: { fontSize: 15, textAlign: 'center', paddingVertical: 32 },
  card: { paddingVertical: 4 },
  divider: { height: StyleSheet.hairlineWidth },
  savedBanner: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: RADIUS.sm,
  },
  savedBannerText: { fontWeight: '600' },
});
